"use client"

import Image from "next/image"
import { useRouter } from "next/navigation"
import { signOut } from "next-auth/react"
import PropTypes from "prop-types"
import { useEffect } from "react"
import {
  RiAccountCircleLine,
  RiChatAiLine,
  RiGitRepositoryLine,
  RiGroupLine,
  RiHandCoinLine,
  RiHome4Line,
  RiInboxArchiveLine,
  RiLogoutCircleLine,
  RiMenuFoldLine,
  RiMenuUnfoldLine,
  RiPaletteLine,
  RiRoadsterLine,
  RiSearchLine,
  RiShoppingBasketLine,
} from "react-icons/ri"

import { useLocaleConfig } from "@/providers/localeConfig"
import { useLocaleStrings } from "@/providers/localeStrings/client"
import { useSideNavConfig } from "@/providers/sideNavConfig"
import theme from "@/theme"
import { setCookie } from "@/utils/cookies"
import { isAdmLeadUser, isIdmSpringer, isIdmUser } from "@/utils/options"
import { getPriceFormatted } from "@/utils/prices"
import { removeUnits } from "@/utils/strings"

import { NavigationItem } from "./NavigationItem"
import { LogoLink, MainNavigation, Nav, ScrollableItems, SubNavigationWrapper } from "./styled"

const getLogoSizes = (logo) => ({
  big: { width: removeUnits(logo.big.width), height: removeUnits(logo.big.height) },
  small: { width: removeUnits(logo.small.width), height: removeUnits(logo.small.height) },
})

export const Navigation = ({ customer, customerCart, user, className, refresh = true }) => {
  const {
    NAVIGATION_HOME_TITLE,
    NAVIGATION_HOME_SUBTITLE,
    NAVIGATION_HOME_SUBTITLE_IDM,
    NAVIGATION_CLIENTS_TITLE,
    NAVIGATION_CLIENTS_SUBTITLE,
    NAVIGATION_PRODUCTS_TITLE,
    NAVIGATION_PRODUCTS_SUBTITLE,
    NAVIGATION_TOURS_TITLE,
    NAVIGATION_TOURS_SUBTITLE,
    NAVIGATION_SEARCH_TITLE,
    NAVIGATION_SEARCH_SUBTITLE,
    NAVIGATION_LIST_TITLE,
    NAVIGATION_LIST_SUBTITLE,
    NAVIGATION_EXPANDER_TITLE,
    NAVIGATION_SHUTDOWN_TITLE,
    NAVIGATION_MIX_PROCUTS_TITLE,
    NAVIGATION_MIX_PROCUTS_SUBTITLE,
    NAVIGATION_DOWNLOAD_CENTER_TITLE,
    NAVIGATION_DOWNLOAD_CENTER_SUBTITLE,
    NAVIGATION_ASSISTANT_TITLE,
    NAVIGATION_ASSISTANT_SUBTITLE,
    CART_ARTICLES,
    CART_IS_EMPTY,
  } = useLocaleStrings()

  const router = useRouter()
  const locale = useLocaleConfig().locale ?? "de-DE"
  const { sideNavOpen, setSideNavOpen } = useSideNavConfig()
  const { icons: iconSize, logo } = theme.widgets.navigation.sizes
  const logoSize = getLogoSizes(logo)

  const createQueryStringFromCache = (colorId, productId, collectionId) => {
    const params = new URLSearchParams()
    params.set("colorId", colorId)
    params.set("productId", productId)
    params.set("collectionId", collectionId)

    return params.toString()
  }

  const isIdm = !!isIdmUser(user?.role)
  const withOrderReturn = customer?.orderReturn
  const withOrderCashReturn = isIdm && customer?.orderCashReturn
  const showCartButton = !customer?.hasIdmRestrictions

  const [productId, collectionId, colorSysId] = customer?.mixProducts?.split("--") || []
  const searchTerm = customer?.articleSearch
  const [categoryId, searchParams] = customer?.productSearch?.split("--") || []

  let mixProductCachedSearch = null
  let articleCachedSearch = null
  let productCachedSearch = null

  if (productId && collectionId && colorSysId) {
    mixProductCachedSearch = createQueryStringFromCache(colorSysId, productId, collectionId)
  }

  if (searchTerm) {
    articleCachedSearch = `?searchTerm=${searchTerm}`
  }

  if (categoryId) {
    productCachedSearch = `/${categoryId}`
  }

  if (searchParams) {
    if (productCachedSearch) {
      productCachedSearch += `?${searchParams}`
    } else {
      productCachedSearch = `?${searchParams}`
    }
  }

  const cartTitle =
    customerCart?.customLineItems?.length > 0
      ? `${customerCart.customLineItems.length} ${CART_ARTICLES}: ${getPriceFormatted(
          (customerCart.taxedPrice?.totalGross?.centAmount || 0) +
            (customerCart.custom?.fields?.freightSurcharge?.centAmount || 0),
          customerCart.taxedPrice?.totalGross?.currencyCode,
          locale,
        )}`
      : CART_IS_EMPTY

  const cartSubtitle = `${customer?.customerNumber} ${customer?.customerName}`

  const toggleSideNav = () => {
    setSideNavOpen(!sideNavOpen)
  }

  // I know this is not the best way to do it, but it works and that is what counts!
  // Does not work on mac, since mac for some reason has different key codes
  // https://asawicki.info/nosense/doc/devices/keyboard/key_codes.html
  useEffect(() => {
    const handleKeyDownNav = (event) => {
      if (event.keyCode === 112 || event.code === "F1") {
        event.preventDefault()
        event.stopPropagation()
        router.push("/" + locale + "/customers")
      } else if (event.keyCode === 113 || event.code === "F2") {
        event.preventDefault()
        event.stopPropagation()
        router.push("/" + locale + "/cart")
      } else if (event.keyCode === 114 || event.code === "F3") {
        event.preventDefault()
        event.stopPropagation()
        router.push(articleCachedSearch ? "/" + locale + "/search" + articleCachedSearch : "/" + locale + "/search")
      }

      return
    }

    window.addEventListener("keydown", handleKeyDownNav)

    return () => {
      window.removeEventListener("keydown", handleKeyDownNav)
    }
  }, [articleCachedSearch, locale, router])

  const handleSignOut = async () => {
    if (isIdmSpringer(user?.role)) {
      setCookie("branches", "springerBranchId", "")
    }

    await signOut()
  }

  return (
    <Nav className={className}>
      <MainNavigation>
        <LogoLink href={"/" + locale} $sideNavOpen={sideNavOpen}>
          <Image
            src="/images/logo.svg"
            alt="einzA logo image"
            width={sideNavOpen ? logoSize.big.width : logoSize.small.width}
            height={sideNavOpen ? logoSize.big.height : logoSize.small.height}
            quality={100}
          />
        </LogoLink>
        <ScrollableItems $sideNavOpen={sideNavOpen}>
          <NavigationItem
            href={"/" + locale}
            title={NAVIGATION_HOME_TITLE}
            subtitle={!isIdm ? NAVIGATION_HOME_SUBTITLE : NAVIGATION_HOME_SUBTITLE_IDM}
            $sideNavOpen={sideNavOpen}
            isHome
            testId="nav-home"
          >
            <RiHome4Line size={iconSize.normal} />
          </NavigationItem>
          <NavigationItem
            href={`/${locale}/customers/${customer?.customerNumber ?? ""}`}
            title={NAVIGATION_CLIENTS_TITLE}
            subtitle={NAVIGATION_CLIENTS_SUBTITLE}
            $sideNavOpen={sideNavOpen}
            includedPaths={["/customers"]}
            testId="nav-customers"
          >
            <RiAccountCircleLine size={iconSize.normal} />
          </NavigationItem>
          <NavigationItem
            href={productCachedSearch ? "/" + locale + "/products" + productCachedSearch : "/" + locale + "/products"}
            title={NAVIGATION_PRODUCTS_TITLE}
            subtitle={NAVIGATION_PRODUCTS_SUBTITLE}
            $sideNavOpen={sideNavOpen}
            includedPaths={["/product"]}
            testId="nav-products"
          >
            <RiGitRepositoryLine size={iconSize.normal} />
          </NavigationItem>
          <NavigationItem
            href={articleCachedSearch ? "/" + locale + "/search" + articleCachedSearch : "/" + locale + "/search"}
            title={NAVIGATION_SEARCH_TITLE}
            subtitle={NAVIGATION_SEARCH_SUBTITLE}
            $sideNavOpen={sideNavOpen}
            includedPaths={["search"]}
            testId="nav-article-search"
          >
            <RiSearchLine size={iconSize.normal} />
          </NavigationItem>
          {!isIdm && (
            <NavigationItem
              href={"/" + locale + "/tours"}
              title={NAVIGATION_TOURS_TITLE}
              subtitle={NAVIGATION_TOURS_SUBTITLE}
              $sideNavOpen={sideNavOpen}
              testId="nav-tours"
            >
              <RiRoadsterLine size={iconSize.normal} />
            </NavigationItem>
          )}
          <NavigationItem
            href={
              mixProductCachedSearch
                ? "/" + locale + "/mix-products?" + mixProductCachedSearch
                : "/" + locale + "/mix-products"
            }
            title={NAVIGATION_MIX_PROCUTS_TITLE}
            subtitle={NAVIGATION_MIX_PROCUTS_SUBTITLE}
            $sideNavOpen={sideNavOpen}
            includedPaths={["/mix-products"]}
            testId="nav-mix-products"
          >
            <RiPaletteLine size={iconSize.normal} />
          </NavigationItem>
          <NavigationItem
            href={"/" + locale + "/download-center"}
            title={NAVIGATION_DOWNLOAD_CENTER_TITLE}
            subtitle={NAVIGATION_DOWNLOAD_CENTER_SUBTITLE}
            $sideNavOpen={sideNavOpen}
            includedPaths={["/download-center"]}
            testId="nav-download-center"
          >
            <RiInboxArchiveLine size={iconSize.normal} />
          </NavigationItem>

          {user?.enableAssistant && (
            <NavigationItem
              href={"/" + locale + "/assistant"}
              title={NAVIGATION_ASSISTANT_TITLE}
              subtitle={NAVIGATION_ASSISTANT_SUBTITLE}
              $sideNavOpen={sideNavOpen}
            >
              <RiChatAiLine size={iconSize.normal} />
            </NavigationItem>
          )}

          {isAdmLeadUser(user?.role) && (
            <NavigationItem
              href={"/" + locale + "/adm-list"}
              title={NAVIGATION_LIST_TITLE}
              subtitle={NAVIGATION_LIST_SUBTITLE}
              $sideNavOpen={sideNavOpen}
            >
              <RiGroupLine size={iconSize.normal} />
            </NavigationItem>
          )}

          <SubNavigationWrapper>
            {showCartButton && (
              <NavigationItem
                secondary
                seo
                href={
                  withOrderReturn
                    ? "/" + locale + "/return"
                    : withOrderCashReturn
                      ? "/" + locale + "/cash-return"
                      : "/" + locale + "/cart"
                }
                title={cartTitle}
                subtitle={cartSubtitle}
                $sideNavOpen={sideNavOpen}
                includedPaths={["/return", "/cart"]}
                testId={withOrderReturn ? "nav-return" : withOrderCashReturn ? "nav-cash-return" : "nav-cart"}
              >
                {withOrderReturn || withOrderCashReturn ? (
                  <RiHandCoinLine size={iconSize.normal} />
                ) : (
                  <RiShoppingBasketLine size={iconSize.normal} />
                )}
              </NavigationItem>
            )}
            <NavigationItem
              title={NAVIGATION_EXPANDER_TITLE}
              additional
              handleClick={toggleSideNav}
              $sideNavOpen={sideNavOpen}
            >
              {sideNavOpen ? <RiMenuFoldLine size={iconSize.small} /> : <RiMenuUnfoldLine size={iconSize.small} />}
            </NavigationItem>
            <NavigationItem
              title={NAVIGATION_SHUTDOWN_TITLE}
              handleClick={() => handleSignOut()}
              additional
              $sideNavOpen={sideNavOpen}
            >
              <RiLogoutCircleLine size={iconSize.small} />
            </NavigationItem>
          </SubNavigationWrapper>
        </ScrollableItems>
      </MainNavigation>
    </Nav>
  )
}

Navigation.propTypes = {
  /** Logged in customer data */
  customer: PropTypes.object,
  /** Customer cart data */
  customerCart: PropTypes.object,
  /** Defines css classes for styling */
  className: PropTypes.string,
}
